"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Eye, EyeOff } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUser } from "@/hooks/useUser";
import { useAuth } from "@/lib/auth-client";
const formSchema = z.object({
  email: z.string().email({
    message: "Invalid email address"
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters"
  })
});
function getSafeRedirectUrl(url: string | null): string {
  if (!url) {
    return "/";
  }
  if (url.startsWith("/") && !url.startsWith("//")) {
    return url;
  }
  return "/";
}
export default function Login() {
  const $ = _c(31);
  const queryClient = useQueryClient();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    signIn
  } = useAuth();
  const returnUrl = getSafeRedirectUrl(searchParams.get("returnUrl"));
  const {
    theme
  } = useTheme();
  useUser({
    redirectTo: returnUrl,
    redirectWhen: "authenticated"
  });
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      resolver: zodResolver(formSchema),
      defaultValues: {
        email: "",
        password: ""
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const form = useForm(t0);
  const onSubmit = async function onSubmit(values) {
    setIsLoading(true);
    const {
      error
    } = await signIn.email({
      email: values.email,
      password: values.password
    }, {
      onSuccess: () => {
        queryClient.clear();
        toast.success("Logged in successfully!");
        router.push(returnUrl);
      },
      onError: _temp
    });
    if (error) {
      toast.error(error.message || "An error occurred");
    }
    setIsLoading(false);
  };
  const t1 = "min-h-screen w-screen bg-white dark:bg-black flex items-center justify-center overflow-hidden";
  const t2 = "w-full max-w-md px-6";
  const t3 = theme === "dark" ? "/brand-mark-dark.png" : "/brand-mark-light.png";
  let t4;
  if ($[1] !== t3) {
    t4 = <div className="mb-8 flex justify-center"><Link href="/" className="inline-block"><Image src={t3} alt="RouterBench" width={100} height={32} priority={true} className="h-auto w-auto" /></Link></div>;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mb-6 flex justify-center"><div className="inline-block px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"><p className="text-xs font-medium text-gray-600 dark:text-gray-400">Admin Portal</p></div></div>;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mb-8 text-center"><h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Sign in</h1><p className="text-sm text-gray-600 dark:text-gray-400">Access admin panel</p></div>;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const T0 = Form;
  const t7 = form.handleSubmit(onSubmit);
  let t8;
  if ($[5] !== form.control) {
    t8 = <FormField control={form.control} name="email" render={_temp2} />;
    $[5] = form.control;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== showPassword) {
    t9 = t10 => {
      const {
        field: field_0
      } = t10;
      return <FormItem><div className="flex items-center justify-between"><FormLabel className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Password</FormLabel><Link href="/forgot-password" className="text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">Forgot?</Link></div><FormControl><div className="relative mt-1.5"><Input placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"} type={showPassword ? "text" : "password"} className="h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-gray-900 dark:focus:border-white focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10" {...field_0} /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></FormControl><FormMessage className="text-xs" /></FormItem>;
    };
    $[7] = showPassword;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== form.control || $[10] !== t9) {
    t10 = <FormField control={form.control} name="password" render={t9} />;
    $[9] = form.control;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== isLoading) {
    t11 = isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in</> : "Continue";
    $[12] = isLoading;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== isLoading || $[15] !== t11) {
    t12 = <Button type="submit" disabled={isLoading} className="w-full mt-6 h-10 rounded-md bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-white font-medium text-sm transition-colors">{t11}</Button>;
    $[14] = isLoading;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t10 || $[18] !== t12 || $[19] !== t7 || $[20] !== t8) {
    t13 = <form onSubmit={t7} className="space-y-4">{t8}{t10}{t12}</form>;
    $[17] = t10;
    $[18] = t12;
    $[19] = t7;
    $[20] = t8;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== T0 || $[23] !== form || $[24] !== t13) {
    t14 = <T0 {...form}>{t13}</T0>;
    $[22] = T0;
    $[23] = form;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6) {
    t15 = <div className={t1}><div className={t2}>{t4}{t5}{t6}{t14}</div></div>;
    $[26] = t14;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
function _temp2(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Email</FormLabel><FormControl><Input placeholder="admin@example.com" type="email" className="mt-1.5 h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-gray-900 dark:focus:border-white focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10" {...field} /></FormControl><FormMessage className="text-xs" /></FormItem>;
}
function _temp(ctx) {
  toast.error(ctx.error.message || "Login failed");
}